import { ArrowDownUp, Play, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAnalyticsClosedLots } from '../../features/analytics/queries';
import type { AnalyticsClosedLotDto } from '../../lib/api/endpoints';
import { formatCurrency } from '../../lib/format/currency';
import { positionReplayHref } from '../../lib/navigation/positionReplay';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Table } from '../ui/Table';
import { TableCell } from '../ui/TableCell';
import { TableHeaderCell } from '../ui/TableHeaderCell';

type LotSort = 'impact' | 'closedAt';

interface ClosedLotsDrilldownProps {
  accountId: string;
  currency: string;
  from: string;
  to: string;
  label: string;
  onClose: () => void;
}

function sortLots(lots: AnalyticsClosedLotDto[], sort: LotSort): AnalyticsClosedLotDto[] {
  return [...lots].sort((left, right) => {
    if (sort === 'closedAt') return right.closedAt.localeCompare(left.closedAt);
    return Math.abs(Number(right.realizedPnl)) - Math.abs(Number(left.realizedPnl));
  });
}

function pnlTone(value: number): 'danger' | 'neutral' | 'success' {
  if (value === 0) return 'neutral';
  return value > 0 ? 'success' : 'danger';
}

function pnlTextClass(value: number): string {
  if (value > 0) return 'text-success';
  if (value < 0) return 'text-danger';
  return 'text-text';
}

export function ClosedLotsDrilldown({
  accountId,
  currency,
  from,
  label,
  onClose,
  to,
}: ClosedLotsDrilldownProps) {
  const closedLots = useAnalyticsClosedLots(accountId, from, to);
  const [symbolFilter, setSymbolFilter] = useState<string | null>(null);
  const [sort, setSort] = useState<LotSort>('impact');
  const headingRef = useRef<HTMLHeadingElement>(null);
  const data = closedLots.data;
  const visibleLots = useMemo(
    () =>
      sortLots(
        (data?.lots ?? []).filter((lot) => symbolFilter === null || lot.symbol === symbolFilter),
        sort,
      ),
    [data?.lots, sort, symbolFilter],
  );

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <section
      className="rounded border border-primary bg-surface p-4"
      aria-labelledby="closed-lots-drilldown-title"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2
            id="closed-lots-drilldown-title"
            ref={headingRef}
            tabIndex={-1}
            className="text-lg font-semibold"
          >
            {label} breakdown
          </h2>
          {data ? (
            <p className="text-sm text-text-muted">
              {data.tradeCount} closed {data.tradeCount === 1 ? 'lot' : 'lots'} across{' '}
              {data.bySymbol.length} {data.bySymbol.length === 1 ? 'symbol' : 'symbols'} | win rate{' '}
              {data.winRate.toFixed(1)}%
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {data ? (
            <Badge size="md" tone={pnlTone(data.totalPnl)}>
              {formatCurrency(data.totalPnl, currency)}
            </Badge>
          ) : null}
          <Button
            className="h-8 w-8 px-0"
            size="sm"
            variant="ghost"
            onClick={onClose}
            aria-label="Close breakdown"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </div>

      {closedLots.isLoading ? (
        <p className="mt-4 text-sm text-text-muted">Loading closed lots...</p>
      ) : closedLots.isError || !data ? (
        <p className="mt-4 text-sm text-danger">Could not load closed lots.</p>
      ) : data.lots.length === 0 ? (
        <p className="mt-4 text-sm text-text-muted">No closed lots in this range.</p>
      ) : (
        <>
          <div className="mt-4">
            <h3 className="text-sm font-medium text-text-muted">P&L by symbol</h3>
            <div
              className="mt-2 flex max-h-40 flex-wrap gap-2 overflow-y-auto"
              aria-label="Filter lots by symbol"
            >
              <SymbolChip
                active={symbolFilter === null}
                label="All"
                count={data.tradeCount}
                onClick={() => setSymbolFilter(null)}
              />
              {data.bySymbol.map((item) => (
                <SymbolChip
                  key={item.symbol}
                  active={symbolFilter === item.symbol}
                  label={item.symbol}
                  count={item.tradeCount}
                  pnl={formatCurrency(item.totalPnl, currency)}
                  pnlClass={pnlTextClass(item.totalPnl)}
                  onClick={() =>
                    setSymbolFilter((current) => (current === item.symbol ? null : item.symbol))
                  }
                />
              ))}
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between gap-3">
            <h3 className="text-sm font-medium text-text-muted">
              {visibleLots.length} {visibleLots.length === 1 ? 'lot' : 'lots'}
              {symbolFilter ? ` in ${symbolFilter}` : ''}
            </h3>
            <button
              type="button"
              onClick={() => setSort((value) => (value === 'impact' ? 'closedAt' : 'impact'))}
              className="inline-flex items-center gap-2 text-sm text-text-muted hover:text-text"
              aria-label={`Sorted by ${sort === 'impact' ? 'largest P&L' : 'close date'}. Change sort.`}
            >
              <ArrowDownUp className="h-3.5 w-3.5" aria-hidden="true" />
              {sort === 'impact' ? 'Largest P&L first' : 'Most recent first'}
            </button>
          </div>

          <div className="mt-2 hidden max-h-[32rem] overflow-auto sm:block">
            <Table className="min-w-full">
              <thead className="sticky top-0 bg-surface text-text-muted">
                <tr>
                  <TableHeaderCell>Symbol</TableHeaderCell>
                  <TableHeaderCell>Opened</TableHeaderCell>
                  <TableHeaderCell>Closed</TableHeaderCell>
                  <TableHeaderCell numeric>Qty</TableHeaderCell>
                  <TableHeaderCell numeric>Entry</TableHeaderCell>
                  <TableHeaderCell numeric>Exit</TableHeaderCell>
                  <TableHeaderCell numeric>P&L</TableHeaderCell>
                  <TableHeaderCell numeric>Hold</TableHeaderCell>
                  <TableHeaderCell>
                    <span className="sr-only">Actions</span>
                  </TableHeaderCell>
                </tr>
              </thead>
              <tbody>
                {visibleLots.map((lot) => (
                  <tr key={lot.id} className="border-t border-border hover:bg-surface-raised">
                    <TableCell className="font-semibold">
                      <Link
                        className="text-primary hover:text-primary-hover"
                        to={`/analytics/symbols/${lot.symbol}?accountId=${accountId}`}
                      >
                        {lot.symbol}
                      </Link>
                    </TableCell>
                    <TableCell className="tabular-nums">{lot.openedAt.slice(0, 10)}</TableCell>
                    <TableCell className="tabular-nums">{lot.closedAt.slice(0, 10)}</TableCell>
                    <TableCell numeric>{lot.quantity}</TableCell>
                    <TableCell numeric>{formatCurrency(lot.entryPrice, currency)}</TableCell>
                    <TableCell numeric>{formatCurrency(lot.exitPrice, currency)}</TableCell>
                    <TableCell
                      numeric
                      className={`font-semibold ${pnlTextClass(Number(lot.realizedPnl))}`}
                    >
                      {formatCurrency(lot.realizedPnl, currency)}
                    </TableCell>
                    <TableCell numeric>{lot.holdingDays}d</TableCell>
                    <TableCell numeric>
                      <Button asChild className="h-8 w-8 px-0" size="sm" variant="ghost">
                        <Link
                          aria-label={`Replay trades for ${lot.symbol}`}
                          to={positionReplayHref(lot.symbol, accountId)}
                        >
                          <Play className="h-4 w-4" aria-hidden="true" />
                        </Link>
                      </Button>
                    </TableCell>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <ul className="mt-2 max-h-[32rem] space-y-2 overflow-y-auto sm:hidden">
            {visibleLots.map((lot) => (
              <li key={lot.id} className="rounded border border-border p-3">
                <div className="flex items-center justify-between gap-2">
                  <Link
                    className="font-semibold text-primary hover:text-primary-hover"
                    to={`/analytics/symbols/${lot.symbol}?accountId=${accountId}`}
                  >
                    {lot.symbol}
                  </Link>
                  <span
                    className={`font-semibold tabular-nums ${pnlTextClass(Number(lot.realizedPnl))}`}
                  >
                    {formatCurrency(lot.realizedPnl, currency)}
                  </span>
                </div>
                <div className="mt-1 text-xs tabular-nums text-text-muted">
                  {lot.openedAt.slice(0, 10)} to {lot.closedAt.slice(0, 10)} | {lot.holdingDays}d
                </div>
                <div className="mt-1 text-xs tabular-nums text-text-muted">
                  {lot.quantity} @ {formatCurrency(lot.entryPrice, currency)} to{' '}
                  {formatCurrency(lot.exitPrice, currency)}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function SymbolChip({
  active,
  count,
  label,
  onClick,
  pnl,
  pnlClass,
}: {
  active: boolean;
  count: number;
  label: string;
  onClick: () => void;
  pnl?: string;
  pnlClass?: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded border px-2 py-1 text-sm tabular-nums ${
        active ? 'border-primary bg-surface-raised' : 'border-border hover:bg-surface-raised'
      }`}
    >
      <span className="font-semibold">{label}</span>
      {pnl ? <span className={pnlClass}>{pnl}</span> : null}
      <span className="text-text-muted">({count})</span>
    </button>
  );
}
